import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type SyncJobState, type SyncSummary } from '@/lib/api';
import { ArrowDownToLine, RefreshCw } from 'lucide-react';

export default function SyncButton() {
  const [showResult, setShowResult] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  // Poll status when modal is open OR a job is known running. Poll fast (2s)
  // while running, otherwise off.
  const statusQuery = useQuery({
    queryKey: ['sync-status'],
    queryFn: api.syncStatus,
    refetchInterval: (q) => (q.state.data?.current ? 2000 : false),
  });
  const isRunning = !!statusQuery.data?.current;
  const lastDone = statusQuery.data?.lastCompleted;

  // When a running job finishes, invalidate roster data and pop the result.
  const wasRunning = useWasTrue(isRunning);
  useEffect(() => {
    if (wasRunning && !isRunning && lastDone?.status === 'success') {
      qc.invalidateQueries({ queryKey: ['players'] });
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setShowResult(true);
    }
  }, [wasRunning, isRunning, lastDone, qc]);

  const start = useMutation({
    mutationFn: api.startLastRankSync,
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['sync-status'] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Sync failed to start'),
  });

  return (
    <>
      <button
        onClick={() => {
          if (isRunning) setShowResult(true);
          else start.mutate();
        }}
        disabled={start.isPending}
        className="inline-flex items-center gap-1 rounded border border-indigo-700 px-3 py-1.5 text-sm text-indigo-200 hover:bg-indigo-900/30 disabled:opacity-50"
        title={
          isRunning
            ? 'Sync in progress — click to see progress'
            : 'Pull latest data from LastRank.fun'
        }
      >
        {isRunning ? (
          <RefreshCw size={14} className="animate-spin" />
        ) : (
          <ArrowDownToLine size={14} />
        )}
        {isRunning
          ? `Syncing… ${statusQuery.data?.current?.processed ?? 0}/${statusQuery.data?.current?.total ?? '?'}`
          : 'Sync LastRank'}
      </button>
      {error && (
        <div className="rounded bg-red-900/40 px-3 py-2 text-sm text-red-200">{error}</div>
      )}
      {showResult && statusQuery.data && (
        <SyncStatusModal
          job={statusQuery.data.current ?? statusQuery.data.lastCompleted}
          onClose={() => setShowResult(false)}
        />
      )}
    </>
  );
}

function useWasTrue(v: boolean): boolean {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (v) setSeen(true);
  }, [v]);
  return seen && !v ? true : false;
}

function SyncStatusModal({
  job,
  onClose,
}: {
  job: SyncJobState | null;
  onClose: () => void;
}) {
  if (!job) return null;
  const pct = job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[85vh] w-full max-w-2xl space-y-3 overflow-y-auto rounded-lg border border-slate-800 bg-slate-950 p-5 shadow-2xl">
        <header className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            LastRank sync{' '}
            <span className="text-sm font-normal text-slate-400">#{job.id}</span>
          </h2>
          <button
            onClick={onClose}
            className="rounded border border-slate-700 px-3 py-1 text-sm hover:bg-slate-800"
          >
            Close
          </button>
        </header>

        {job.status === 'running' && (
          <>
            <div className="text-sm text-slate-300">
              {job.message ?? 'Working…'}
              {job.currentPlayer && (
                <> · <span className="text-slate-400">{job.currentPlayer}</span></>
              )}
            </div>
            <div className="h-2 overflow-hidden rounded bg-slate-800">
              <div
                className="h-full bg-indigo-500 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="text-right text-xs text-slate-500">
              {job.processed}/{job.total} ({pct}%)
            </div>
          </>
        )}

        {job.status === 'error' && (
          <div className="rounded bg-red-900/40 px-3 py-2 text-sm text-red-200">
            {job.message}
          </div>
        )}

        {job.result && <SummaryView summary={job.result} />}
      </div>
    </div>
  );
}

function SummaryView({ summary }: { summary: SyncSummary }) {
  const seconds = (summary.durationMs / 1000).toFixed(1);
  return (
    <>
      <div className="grid grid-cols-4 gap-2 text-center text-sm">
        <Stat label="Fetched" value={summary.fetchedCount} accent="text-slate-200" />
        <Stat label="Matched" value={summary.matchedCount} accent="text-emerald-300" />
        <Stat label="Snapshots" value={summary.snapshotsCreated} accent="text-indigo-300" />
        <Stat label="Time" value={`${seconds}s`} accent="text-slate-400" />
      </div>

      {summary.unmatched.length > 0 && (
        <Section title={`Unmatched (${summary.unmatched.length})`} accent="amber">
          <ul className="space-y-0.5 text-xs">
            {summary.unmatched.map((u) => (
              <li key={u.publicId} className="flex justify-between gap-2">
                <span className="truncate">{u.name}</span>
                <a
                  href={`https://lastrank.fun/p/${u.publicId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 text-indigo-400 hover:underline"
                >
                  #{u.publicId}
                </a>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {summary.nameChanges.length > 0 && (
        <Section title={`Name changes (${summary.nameChanges.length})`} accent="indigo">
          <ul className="space-y-0.5 text-xs">
            {summary.nameChanges.map((c) => (
              <li key={c.playerId}>
                <span className="text-slate-500">{c.oldName}</span>
                <span className="mx-1">→</span>
                <span>{c.newName}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {summary.rankChanges.length > 0 && (
        <Section title={`Rank changes (${summary.rankChanges.length})`} accent="indigo">
          <ul className="space-y-0.5 text-xs">
            {summary.rankChanges.map((c) => (
              <li key={c.playerId}>
                {c.name}: <span className="text-slate-500">{c.oldRank}</span>
                <span className="mx-1">→</span>
                <span>{c.newRank}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {summary.professionUpdates.length > 0 && (
        <Section
          title={`Profession assignments (${summary.professionUpdates.length})`}
          accent="emerald"
        >
          <ul className="space-y-0.5 text-xs">
            {summary.professionUpdates.map((c) => (
              <li key={c.playerId}>
                {c.name} → <span className="capitalize">{c.profession}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {summary.errors.length > 0 && (
        <Section title={`Errors (${summary.errors.length})`} accent="red">
          <ul className="space-y-0.5 text-xs text-red-300">
            {summary.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Section>
      )}
    </>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent: string;
}) {
  return (
    <div className="rounded bg-slate-900/60 p-2">
      <div className={`text-xl font-semibold tabular-nums ${accent}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}

const ACCENT_BG: Record<string, string> = {
  amber: 'border-amber-900/50 bg-amber-900/10',
  indigo: 'border-indigo-900/50 bg-indigo-900/10',
  emerald: 'border-emerald-900/50 bg-emerald-900/10',
  red: 'border-red-900/50 bg-red-900/10',
};

function Section({
  title,
  accent,
  children,
}: {
  title: string;
  accent: 'amber' | 'indigo' | 'emerald' | 'red';
  children: React.ReactNode;
}) {
  return (
    <section className={`rounded border ${ACCENT_BG[accent]} p-3`}>
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}
